import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Chip, Dialog } from '@mui/material';
import { MRT_GlobalFilterTextField as GlobalFilterTextField, MRT_ToggleFiltersButton as ToggleFiltersButton, type MRT_ColumnDef } from 'material-react-table';
import { CloseButton, CustomMaterialTable, Loading, Pagination } from 'components';
import { useJobs, usePagination } from 'hooks';
import { JobsDTO } from 'api/jobs';
import { jobsColumns } from './consts';
import { JobDetail } from './components';

export const JobsPage: React.FC = () => {
  const [jobDetail, setJobDetail] = useState<JobsDTO | null>(null);
  const [jobsData, setJobsData] = useState<JobsDTO[]>([]);

  const {
    pagination,
    onPageChange,
    onSizeChange,
    updateFromApiResponse,
    uiPage,
  } = usePagination({
    initialSize: 10,
    rowsPerPageOptions: [10, 20, 50, 100],
  });

  const { data: response, isLoading } = useJobs(pagination.size, pagination.page);

  const allJobs = useMemo(() => jobsData ?? [], [jobsData]);

  useEffect(() => {
    if (response) {
      setJobsData(response?.jobs || []);
      updateFromApiResponse(response.pageResult);
    }
  }, [response, updateFromApiResponse]);

  return (
    <Box sx={{ ml: 3, pr: 3, mt: 3 }}>
      {isLoading ? (
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginTop: '20%',
          }}
        >
          <Loading />
        </Box>
      ) : (
        <>
          <CustomMaterialTable
            columns={jobsColumns as MRT_ColumnDef<JobsDTO>[]}
            data={allJobs}
            enableRowActions
            manualPagination
            rowCount={pagination.size}
            positionActionsColumn='last'
            displayColumnDefOptions={{
              'mrt-row-actions': { size: 130, minSize: 60, header: 'Actions' },
            }}
            renderRowActions={({ row }) => (
              <Button
                variant='text'
                onClick={() => setJobDetail(row.original)}
              >
                View details
              </Button>
            )}
            renderTopToolbar={({ table }) => (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <h3>
                  Jobs <Chip label={pagination.totalRows} />
                </h3>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <GlobalFilterTextField table={table} />
                  <ToggleFiltersButton table={table} />
                </Box>
              </Box>
            )}
            initialState={{
              showGlobalFilter: true,
            }}
            enableStickyHeader
            topToolbarSx={{
              height: 'auto',
              minHeight: 'auto',
              backgroundColor: 'white',
              '& .MuiIconButton-root': { display: 'unset' },
            }}
            containerSx={{
              height: 'calc(100vh - 250px)',
              minHeight: '200px',
              width: 'calc(100vw - 150px)',
              overflowX: 'auto',
              "& thead tr th": {
                padding: "8px 16px",
              },
              "& tbody tr td:last-of-type": {
                paddingLeft: "0px",
              },
            }}
          />
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              marginTop: '10px',
            }}
          >
            <Pagination
              page={uiPage}
              rowsCount={pagination.totalRows}
              rowsPerPage={pagination.size}
              rowsPerPageOptions={[10, 20, 50, 100]}
              onPageChange={onPageChange}
              onRowsPerPageChange={onSizeChange}
            />
          </Box>
        </>
      )}
      <Dialog open={Boolean(jobDetail)} onClose={() => setJobDetail(null)} maxWidth='lg'>
        <Box>
          {jobDetail && (
            <JobDetail jobData={jobDetail}>
              <CloseButton
                onClick={() => setJobDetail(null)}
                sx={{
                  width: '20px',
                  height: '20px',
                  maxHeight: '20px',
                  '& > svg': {
                    width: '20px',
                    height: '20px',
                  },
                }}
              />
            </JobDetail>
          )}
        </Box>
      </Dialog>
    </Box>
  );
};

export default JobsPage;
